import { useMemo, useState, useEffect } from 'react'

import { X, Undo2, Redo2 } from "lucide-react"
import { Button, CircularProgress, Snackbar, Alert, Chip, Typography, Box, Paper, FormControl, Select, MenuItem, Tooltip } from '@mui/material'
import { Chat } from '@mui/icons-material'

import ReplayWorkspace from "../../workspaceReplay2/ReplayWorkspace"

import StudentLabelingSidebar from "../../workspaceReplay2/components/StudentLabelingSidebar"
import StudentLabelingDialogs from "../../workspaceReplay2/components/StudentLabelingDialogs"
import ReplayControls from "../../workspaceReplay2/components/ReplayControls"

import { usePlayback } from "../../workspaceReplay2/hooks/usePlayback"

import { autoSegmentEvents } from '../../workspaceReplay2/utils/autoSegmentation'

import {
  getOrCreateLabelingSession,
  loadLabelingSession,
  saveAllLabelingData,
  completeLabelingSession,
  transformTagsFromDB,
  transformQueryLabelsFromDB,
} from '../../../service/labelingService'

const MIN_SEGMENT_MS = 500

const QUERY_INTENT_LABELS = [
  { id: 'guided', label: 'Guided', description: 'The student demonstrates independent thinking. They have identified what they need help with and are actively steering their learning. Includes: asking a specific question about a concept, identifying a specific problem or confusion, describing what they tried and what went wrong, asking how to approach a specific step, or requesting clarification on a specific point. The key indicator: the student has done cognitive work to formulate what they need.' },
  { id: 'dependent', label: 'Dependent', description: 'The student is offloading cognitive work to the AI with minimal independent effort. Includes: pasting code with no question, vague requests with no specifics ("help", "idk"), pure acknowledgments that delegate next steps ("ok do that", "yeah"), empty messages, requests to just write the code, or pasting instructions without any attempt. The key indicator: the student has NOT done cognitive work to identify what they need.' },
];

const W1_PROFILES = [
  { id: 'cold_start', label: 'Cold Start', color: '#94a3b8', description: 'First query with no prior attempt.' },
  { id: 'oriented', label: 'Oriented', color: '#3b82f6', description: 'Made a genuine attempt — moderate code and effort before asking.' },
  { id: 'struggling', label: 'Struggling', color: '#ef4444', description: 'Extensive effort but no resolution — high runs, errors, edits before asking.' },
]

const W2_PROFILES = [
  { id: 'passive', label: 'Passive', color: '#94a3b8', description: 'Zero new effort since last AI response. Re-queries immediately.' },
  { id: 'iterating', label: 'Iterating', color: '#3b82f6', description: 'Light effort, some edits, low error count. Steady progress.' },
  { id: 'debugging', label: 'Debugging', color: '#f59e0b', description: 'Multiple runs and errors.' },
  { id: 'spinning', label: 'Spinning', color: '#ef4444', description: 'High thinking and edits but barely tests. Writing without executing.' },
]

export default function ReplayModal({ 
  onClose, events, task, testCases = [], starterCode, sessionId, studentUserId, labelerName, studentName = 'Student'
}) {
  // Sidebar tab
  const [sidebarTab, setSidebarTab] = useState('labels') // 'labels' | 'queries' | 'profiles'

  // Panel visibility - locked to autoSegment
  const [activePanel] = useState('autoSegment')

  // Manual labeling state
  const [taggedBlocks, setTaggedBlocks] = useState([])
  const [activeRecording, setActiveRecording] = useState(null)
  const [codebookOpen, setCodebookOpen] = useState(sessionId === 659)
  const [stopRecordingOpen, setStopRecordingOpen] = useState(false)
  const [stopRecordingEndTime, setStopRecordingEndTime] = useState(0)
  const [stopRecordingConfidence, setStopRecordingConfidence] = useState(null)

  // Auto-segment state
  const [autoSegments, setAutoSegments] = useState([])

  // Query labels state
  const [queryLabels, setQueryLabels] = useState({})
  const [selectedQueryId, setSelectedQueryId] = useState(null)

  // Profile labels state
  const [profileLabels, setProfileLabels] = useState({})
  const [selectedProfileQueryId, setSelectedProfileQueryId] = useState(null)

  // Undo/Redo state
  const [undoStack, setUndoStack] = useState([])
  const [redoStack, setRedoStack] = useState([])

  // Boundary drag
  const [boundaryDragStarted, setBoundaryDragStarted] = useState(false)

  // Segment looping
  const [loopingSegment, setLoopingSegment] = useState(null)

  // Edit tag state
  const [editingBlock, setEditingBlock] = useState(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)

  // DB state
  const [labelingSessionId, setLabelingSessionId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  // Pending saved tags
  const [pendingSavedTags, setPendingSavedTags] = useState(null)

  const [openDropdownId, setOpenDropdownId] = useState(null)
  const [openProfileDropdownId, setOpenProfileDropdownId] = useState(null)
  const [showQueryInfo, setShowQueryInfo] = useState(false)
  const [showProfileInfo, setShowProfileInfo] = useState(false)

  // Calculate start time and duration from events
  const { startTime, duration, filteredEvents } = useMemo(() => {
    if (events.length === 0) {
      return { startTime: 0, duration: 0, filteredEvents: [] }
    }

    const firstEvent = events[0]
    const start = firstEvent.timestamp

    const allPassedIndex = events.findIndex(e => {
      if (e.type !== 'TEST_CASE_RESULT') return false
      const { passed_count, total_tests } = e.payload || {}
      return passed_count === total_tests && total_tests > 0
    })

    const filtered = allPassedIndex !== -1 
      ? events.slice(0, allPassedIndex + 1)
      : events

    const lastEvent = filtered[filtered.length - 1]
    const end = lastEvent.timestamp

    return {
      startTime: start,
      duration: end - start,
      filteredEvents: filtered
    }
  }, [events])

  useEffect(() => {
    const handleClickOutside = () => {
      setShowQueryInfo(false)
      setShowProfileInfo(false)
      setOpenDropdownId(null)
      setOpenProfileDropdownId(null)
    }
    window.addEventListener('click', handleClickOutside)
    return () => window.removeEventListener('click', handleClickOutside)
  }, [])

  // Extract LLM queries from telemetry events
  const chatQueries = useMemo(() => {
    return filteredEvents
     .filter(e => e.type === 'CHAT_QUERY' && e.payload?.text)
      .map((e, index) => ({
        id: `query-${index}-${e.timestamp}`,
        index,
        time: e.timestamp - startTime,
        timestamp: e.timestamp,
        text: e.payload.text,
      }))
  }, [filteredEvents, startTime])

  // Compute query windows for profile labeling
  const queryWindows = useMemo(() => {
    if (!chatQueries || chatQueries.length === 0) return []

    const sorted = [...chatQueries].sort((a, b) => a.time - b.time)
    const labeledSegments = autoSegments.filter(s => s.behavior && !s.isSystemEvent)

    return sorted.map((query, index) => {
      const isFirst = index === 0
      const windowStart = isFirst ? 0 : sorted[index - 1].time
      const windowEnd = query.time
      const windowDuration = windowEnd - windowStart

      // Find auto-segments that overlap this window
      const segmentsInWindow = autoSegments
        .filter(seg => seg.startTime < windowEnd && seg.endTime > windowStart)
        .map(seg => ({
          ...seg,
          overlapStart: Math.max(seg.startTime, windowStart),
          overlapEnd: Math.min(seg.endTime, windowEnd),
        }))
        .sort((a, b) => a.overlapStart - b.overlapStart)

      // Build behavior summary from segments in window
      const summaryMap = {}
      segmentsInWindow.forEach(seg => {
        if (!seg.behavior) return
        const duration = seg.overlapEnd - seg.overlapStart
        const id = seg.behavior.id
        if (!summaryMap[id]) {
          summaryMap[id] = {
            behavior: seg.behavior,
            totalDuration: 0,
            count: 0,
            earliestStart: seg.overlapStart
          }
        }
        summaryMap[id].totalDuration += duration
        summaryMap[id].count += 1
        if (seg.overlapStart < summaryMap[id].earliestStart) {
          summaryMap[id].earliestStart = seg.overlapStart
        }
      })
      const behaviorSummary = Object.values(summaryMap).sort((a, b) => a.earliestStart - b.earliestStart)

      return {
        queryId: query.id,
        queryIndex: index,
        queryText: query.text || 'No query text',
        queryTime: query.time,
        windowType: isFirst ? 'W1' : 'W2',
        windowStart,
        windowEnd,
        windowDuration,
        segments: segmentsInWindow,
        behaviorSummary,
        profiles: isFirst ? W1_PROFILES : W2_PROFILES,
      }
    })
  }, [chatQueries, autoSegments])

  const labeledProfileCount = Object.keys(profileLabels).length

  const playback = usePlayback(duration)

  // Loop within active segment
  useEffect(() => {
    if (!loopingSegment || !playback.isPlaying) return
    if (playback.currentTime >= loopingSegment.endTime) {
      playback.seek(loopingSegment.startTime)
    }
  }, [playback.currentTime, loopingSegment, playback.isPlaying])

  // Generate auto-segments
  useEffect(() => {
    if (activePanel === 'autoSegment' && filteredEvents.length > 0 && duration > 0 && autoSegments.length === 0) {
      let segments = autoSegmentEvents(filteredEvents, startTime, duration)

      if (segments.length > 0) {
        const lastSegment = segments[segments.length - 1]
        if (lastSegment.endTime - lastSegment.startTime < 1000) {
          segments = segments.slice(0, -1)
        }
      }

      // For Batch 7 (batch 11), auto-apply suggested behaviors
      if (sessionId === 654) {
        segments = segments.map(seg => ({
          ...seg,
          behavior: seg.suggestedBehavior || seg.behavior || null,
          confidence: seg.suggestedBehavior ? 3 : seg.confidence,
        }))
      }

      setAutoSegments(segments)
    }
  }, [activePanel, filteredEvents, startTime, duration, autoSegments.length, sessionId])

  // Initialize labeling session
  useEffect(() => {
    const initializeLabelingSession = async () => {
      if (!sessionId || !studentUserId || !labelerName) return

      try {
        const labelingSession = await getOrCreateLabelingSession(
          sessionId,
          studentUserId,
          labelerName,
          null
        )

        setLabelingSessionId(labelingSession.id)

        if (labelingSession.id) {
          const existingData = await loadLabelingSession(labelingSession.id)
          
          if (existingData?.tags?.length > 0) {
            const transformedTags = transformTagsFromDB(existingData.tags)
            setPendingSavedTags(transformedTags)
          }

          if (existingData?.queryLabels?.length > 0) {
            const transformedQueryLabels = transformQueryLabelsFromDB(existingData.queryLabels)
            setQueryLabels(transformedQueryLabels)
          }

          // Load profile labels if present
          if (existingData?.profileLabels && Object.keys(existingData.profileLabels).length > 0) {
            const simple = {}
            Object.entries(existingData.profileLabels).forEach(([queryId, data]) => {
              simple[queryId] = typeof data === 'string' ? data : data.profileId
            })
            setProfileLabels(simple)
}
        }
      } catch (err) {
        console.error('Error initializing labeling session:', err)
        setSnackbar({ open: true, message: 'Failed to initialize session', severity: 'error' })
      }
    }

    initializeLabelingSession()
  }, [sessionId, studentUserId, labelerName])

  // Apply saved tags onto auto-segments once both are ready
  useEffect(() => {
    if (!pendingSavedTags || autoSegments.length === 0) return

    setAutoSegments(prev => 
      prev.map(segment => {
        const matchingTag = pendingSavedTags.find(tag =>
          Math.abs(tag.startTime - segment.startTime) < 2000 &&
          Math.abs(tag.endTime - segment.endTime) < 2000
        )
        if (matchingTag) {
          return {
            ...segment,
            behavior: matchingTag.behavior,
            confidence: matchingTag.confidence,
            thinkingSubcategory: matchingTag.thinkingSubcategory,
            dbId: matchingTag.dbId,
          }
        }
        return segment
      })
    )

    setPendingSavedTags(null)
  }, [pendingSavedTags, autoSegments.length])

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000)
    const m = Math.floor(totalSeconds / 60)
    const s = totalSeconds % 60
    return `${m}:${s < 10 ? '0' : ''}${s}`
  }

  // Undo/Redo helpers
  const updateAutoSegments = (updater) => {
    setUndoStack(prev => [...prev, autoSegments])
    setRedoStack([])
    setAutoSegments(updater)
  }

  const handleUndo = () => {
    if (undoStack.length === 0) return
    const previous = undoStack[undoStack.length - 1]
    setRedoStack(prev => [...prev, autoSegments])
    setAutoSegments(previous)
    setUndoStack(prev => prev.slice(0, -1))
  }

  const handleRedo = () => {
    if (redoStack.length === 0) return
    const next = redoStack[redoStack.length - 1]
    setUndoStack(prev => [...prev, autoSegments])
    setAutoSegments(next)
    setRedoStack(prev => prev.slice(0, -1))
  }

  const handleAdjustBoundary = (index, newTime) => {
    if (!boundaryDragStarted) {
      setUndoStack(prev => [...prev, autoSegments])
      setRedoStack([])
      setBoundaryDragStarted(true)
    }

    setAutoSegments(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], endTime: newTime }
      updated[index + 1] = { ...updated[index + 1], startTime: newTime }
      return updated
    })
  }

  useEffect(() => {
    const handleMouseUp = () => {
      if (boundaryDragStarted) setBoundaryDragStarted(false)
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [boundaryDragStarted])

  const handleSliceSegment = () => {
    const time = playback.currentTime
    const segmentIndex = autoSegments.findIndex(
      s => time > s.startTime && time < s.endTime
    )
    if (segmentIndex === -1) return

    const segment = autoSegments[segmentIndex]
    if (time - segment.startTime < MIN_SEGMENT_MS || segment.endTime - time < MIN_SEGMENT_MS) return

    updateAutoSegments(prev => {
      const updated = [...prev]
      const left = { ...segment, id: `${segment.id}-L`, endTime: time }
      const right = { 
        ...segment, 
        id: `${segment.id}-R`, 
        startTime: time, 
        behavior: null, 
        confidence: null, 
        thinkingSubcategory: null 
      }
      updated.splice(segmentIndex, 1, left, right)
      return updated
    })
  }

  const isAutoMode = activePanel === 'autoSegment'
  const showLabeling = activePanel === 'manualLabeling' || activePanel === 'autoSegment'

  const handleStartRecording = (behavior) => {
    setActiveRecording({ behavior, startTime: playback.currentTime })
  }

  const handleStopRecording = () => {
    if (!activeRecording) return
    setStopRecordingEndTime(playback.currentTime)
    setStopRecordingConfidence(null)
    setStopRecordingOpen(true)
  }

  const handleStopRecordingConfirm = (thinkingSubcategory) => {
    if (!activeRecording || !stopRecordingConfidence) return
    
    const newBlock = {
      id: Date.now(),
      behavior: activeRecording.behavior,
      startTime: activeRecording.startTime,
      endTime: stopRecordingEndTime,
      confidence: stopRecordingConfidence,
      thinkingSubcategory: thinkingSubcategory || null,
    }
    
    setTaggedBlocks(prev => [...prev, newBlock].sort((a, b) => a.startTime - b.startTime))
    setActiveRecording(null)
    setStopRecordingOpen(false)
    setStopRecordingConfidence(null)
  }

  const handleStopRecordingCancel = () => {
    setStopRecordingOpen(false)
    setStopRecordingConfidence(null)
  }

  const handleDeleteBlock = (blockId) => {
    if (isAutoMode) {
      const segment = autoSegments.find(s => s.id === blockId)
      if (!segment) return

      if (segment.behavior !== null) {
        updateAutoSegments(prev => 
          prev.map(s => s.id === blockId ? { ...s, behavior: null, confidence: null, thinkingSubcategory: null } : s)
        )
      } else {
        updateAutoSegments(prev => {
          const index = prev.findIndex(s => s.id === blockId)
          if (index === -1) return prev
          
          const updated = [...prev]
          const removed = updated[index]

          if (index > 0) {
            updated[index - 1] = { ...updated[index - 1], endTime: removed.endTime }
          } else if (index < updated.length - 1) {
            updated[index + 1] = { ...updated[index + 1], startTime: removed.startTime }
          }

          updated.splice(index, 1)
          return updated
        })
      }
    } else {
      setTaggedBlocks(prev => prev.filter(b => b.id !== blockId))
    }
  }

  const handleBlockClick = (block) => {
    playback.seek(block.startTime)
    setLoopingSegment(block)
    if (!playback.isPlaying) playback.togglePlay()
  }

  const handleManualSeek = (time) => {
    setLoopingSegment(null)
    playback.seek(time)
  }

  const handleEditBlock = (block) => {
    setEditingBlock({ ...block })
    setEditDialogOpen(true)
  }

  const handleEditConfirm = (updatedBlock) => {
    if (isAutoMode) {
      updateAutoSegments(prev => 
        prev.map(s => s.id === updatedBlock.id ? updatedBlock : s)
      )
    } else {
      setTaggedBlocks(prev => 
        prev.map(b => b.id === updatedBlock.id ? updatedBlock : b)
          .sort((a, b) => a.startTime - b.startTime)
      )
    }
    setEditDialogOpen(false)
    setEditingBlock(null)
  }

  const handleEditCancel = () => {
    setEditDialogOpen(false)
    setEditingBlock(null)
  }

  const handleAssignBehavior = (segmentId, behavior, thinkingSubcategory = null) => {
    updateAutoSegments(prev => 
      prev.map(s => s.id === segmentId 
        ? { ...s, behavior, confidence: 3, thinkingSubcategory } 
        : s
      )
    )
  }

  // Query handlers
  const handleQueryClick = (query) => {
    setSelectedQueryId(query.id)
    playback.seek(query.time)
  }

  const handleUpdateQueryLabel = (queryId, labelId) => {
    setQueryLabels(prev => ({ ...prev, [queryId]: labelId }))
  }

  // Profile handlers
  const handleProfileWindowClick = (window) => {
    setSelectedProfileQueryId(window.queryId)
    playback.seek(window.windowStart)
  }

  const handleUpdateProfileLabel = (queryId, profileId) => {
    setProfileLabels(prev => ({ ...prev, [queryId]: profileId }))
  }

  const currentBlocks = isAutoMode 
    ? autoSegments.filter(s => !s.isSystemEvent) 
    : taggedBlocks

  const labeledCount = isAutoMode 
    ? autoSegments.filter(s => s.behavior && !s.isSystemEvent).length
    : taggedBlocks.length

  const labeledQueryCount = Object.keys(queryLabels).length

  // Build save payload (shared between save and submit)
  const buildSavePayload = () => {
    const blocksToSave = isAutoMode 
      ? autoSegments.filter(s => s.behavior && !s.isSystemEvent) 
      : taggedBlocks

    // Enrich queryLabels with metadata from chatQueries
    const enrichedQueryLabels = {}
    Object.entries(queryLabels).forEach(([queryId, labelId]) => {
      const query = chatQueries.find(q => q.id === queryId)
      enrichedQueryLabels[queryId] = {
        labelId,
        queryIndex: query?.index ?? null,
        queryTimestamp: query?.timestamp ?? null,
        queryText: query?.text ?? null,
      }
    })

    // Enrich profileLabels with metadata
    const enrichedProfileLabels = {}
    Object.entries(profileLabels).forEach(([queryId, profileId]) => {
      const window = queryWindows.find(w => w.queryId === queryId)
      const query = chatQueries.find(q => q.id === queryId)
      enrichedProfileLabels[queryId] = {
        profileId,
        windowType: window?.windowType ?? null,
        windowStart: window?.windowStart ?? null,
        windowEnd: window?.windowEnd ?? null,
        queryIndex: query?.index ?? null,
        queryTimestamp: query?.timestamp ?? null,
        queryText: query?.text ?? null,
      }
    })

    return {
      tags: blocksToSave.map(tag => ({
        id: tag.id,
        dbId: tag.dbId,
        behavior: tag.behavior,
        startTime: tag.startTime,
        endTime: tag.endTime,
        confidence: tag.confidence || 3,
        thinkingSubcategory: tag.thinkingSubcategory,
      })),
      patterns: [],
      queryLabels: enrichedQueryLabels,
      profileLabels: enrichedProfileLabels,
      sessionNotes: '',
      isFlagged: false,
    }
  }

  const handleSave = async () => {
    if (!labelingSessionId) {
      setSnackbar({ open: true, message: 'No labeling session found', severity: 'error' })
      return
    }

    try {
      setSaving(true)
      const result = await saveAllLabelingData(labelingSessionId, buildSavePayload())

      if (result.tags && !isAutoMode) {
        setTaggedBlocks(transformTagsFromDB(result.tags))
      }

      const allLabeled = isAutoMode
        ? autoSegments.filter(s => !s.isSystemEvent).every(s => s.behavior !== null)
        : taggedBlocks.length > 0

      if (allLabeled) {
        await completeLabelingSession(labelingSessionId, {
          confidence: 3,
          submitNotes: `Auto-completed: all segments labeled (${isAutoMode ? 'auto' : 'manual'} mode)`,
          sessionNotes: '',
          isFlagged: false,
        })
        setSnackbar({ open: true, message: 'All segments labeled — session completed!', severity: 'success' })
      } else {
        setSnackbar({ open: true, message: 'Progress saved!', severity: 'success' })
      }
    } catch (err) {
      console.error('Error saving:', err)
      setSnackbar({ open: true, message: 'Failed to save', severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = async () => {
    if (!labelingSessionId) {
      setSnackbar({ open: true, message: 'No labeling session found', severity: 'error' })
      return
    }

    try {
      setSaving(true)
      await saveAllLabelingData(labelingSessionId, buildSavePayload())
      await completeLabelingSession(labelingSessionId, {
        confidence: 3,
        submitNotes: `Student self-labeled session (${isAutoMode ? 'auto' : 'manual'} mode)`,
        sessionNotes: '',
        isFlagged: false,
      })

      setSnackbar({ open: true, message: 'Labels submitted!', severity: 'success' })
      setTimeout(() => onClose(), 1000)
    } catch (err) {
      console.error('Error submitting:', err)
      setSnackbar({ open: true, message: 'Failed to submit', severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  // ============================================
  // RENDER QUERIES TAB
  // ============================================
  const renderQueriesTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ 
        padding: '10px 12px', borderBottom: '1px solid #e5e7eb', 
        backgroundColor: '#f9fafb', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Student Queries ({chatQueries.length})
            </span>
            <div style={{ position: 'relative' }}>
              <span
                onClick={(e) => { e.stopPropagation(); setShowQueryInfo(prev => !prev) }}
                style={{
                  width: '16px', height: '16px', borderRadius: '50%',
                  backgroundColor: showQueryInfo ? '#3b82f6' : '#e5e7eb',
                  color: showQueryInfo ? '#fff' : '#6b7280',
                  fontSize: '10px', fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s', userSelect: 'none', flexShrink: 0,
                }}
              >
                ?
              </span>

              {showQueryInfo && (
                <div style={{
                  position: 'absolute', top: '22px', right: -120, zIndex: 100,
                  backgroundColor: '#fff', borderRadius: '10px', width: '280px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
                  padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px',
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                    Query Type Labels
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.4, padding: '8px', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #f1f5f9' }}>
                    <strong style={{ color: '#374151' }}>Precedence:</strong> If the query introduces any new substantive content (question, code, error, concept inquiry), classify by content (d/i/u). Use Follow-up only for pure conversational reactions with no new content. Use Nothing only for content-free queries that aren\'t follow-ups.
                  </div>
                  {QUERY_INTENT_LABELS.map((label, i) => (
                    <div key={label.id} style={{
                      paddingBottom: i < QUERY_INTENT_LABELS.length - 1 ? '10px' : 0,
                      borderBottom: i < QUERY_INTENT_LABELS.length - 1 ? '1px solid #f1f5f9' : 'none',
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: '#1d4ed8', marginBottom: '2px' }}>
                        {label.label}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.4 }}>
                        {label.description}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <span style={{
            fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px',
            backgroundColor: labeledQueryCount === chatQueries.length && chatQueries.length > 0 ? '#dcfce7' : '#f3f4f6',
            color: labeledQueryCount === chatQueries.length && chatQueries.length > 0 ? '#166534' : '#6b7280',
          }}>
            {labeledQueryCount}/{chatQueries.length} labeled
          </span>
        </div>
      </div>

      {/* Query list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {chatQueries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: '#9ca3af', border: '2px dashed #e5e7eb', borderRadius: '8px' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.5 }}>💬</div>
            <div style={{ fontSize: '13px' }}>No LLM queries found in this session</div>
          </div>
        ) : (
          chatQueries.map((query, index) => {
            const isSelected = selectedQueryId === query.id
            const currentLabel = queryLabels[query.id]
            const labelInfo = QUERY_INTENT_LABELS.find(l => l.id === currentLabel)

            return (
              <div
                key={query.id}
                onClick={() => handleQueryClick(query)}
                style={{
                  padding: '10px 12px',
                  border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: isSelected ? '#eff6ff' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#374151' }}>
                      {index + 1}.)
                    </span>
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>{formatTime(query.time)}</span>
                  </div>
                </div>

                {/* Query text */}
                <div style={{
                  fontSize: '12px', color: '#374151', marginBottom: '10px', lineHeight: 1.4,
                  display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                }}>
                  {query.text || 'No query text'}
                </div>

                {/* Custom Dropdown */}
                <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                  <div
                    onClick={() => setOpenDropdownId(openDropdownId === query.id ? null : query.id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
                      border: `1.5px solid ${currentLabel ? '#93c5fd' : '#e2e8f0'}`,
                      backgroundColor: '#fff',
                      color: currentLabel ? '#1d4ed8' : '#94a3b8',
                      fontWeight: currentLabel ? 600 : 400,
                      fontSize: '13px',
                      transition: 'all 0.15s',
                      userSelect: 'none',
                    }}
                  >
                    <span>{currentLabel ? labelInfo?.label : 'Select'}</span>
                    <span style={{ 
                      fontSize: '10px', color: '#94a3b8',
                      transform: openDropdownId === query.id ? 'rotate(0deg)' : 'rotate(180deg)',
                      transition: 'transform 0.15s'
                    }}>▲</span>
                  </div>

                  {openDropdownId === query.id && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                      backgroundColor: '#fff', borderRadius: '8px', marginTop: '4px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                      border: '1px solid #e2e8f0', overflow: 'hidden',
                    }}>
                      {QUERY_INTENT_LABELS.map((label, i) => {
                        const isSelected = currentLabel === label.id
                        return (
                          <div
                            key={label.id}
                            onClick={() => {
                              handleUpdateQueryLabel(query.id, label.id)
                              setOpenDropdownId(null)
                            }}
                            style={{
                              padding: '10px 14px', cursor: 'pointer',
                              backgroundColor: 'transparent',
                              borderBottom: i < QUERY_INTENT_LABELS.length - 1 ? '1px solid #f1f5f9' : 'none',
                              transition: 'background 0.1s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                          <div style={{ fontSize: '13px', fontWeight: 700, color: isSelected ? '#1d4ed8' : '#374151' }}>                              {label.label}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.3, marginTop: '2px' }}>
                              {label.description}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )

  // ============================================
  // RENDER PROFILES TAB
  // ============================================
  const renderProfilesTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ 
        padding: '10px 12px', borderBottom: '1px solid #e5e7eb', 
        backgroundColor: '#f9fafb', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Behavioral Profiles ({queryWindows.length})
            </span>
            <div style={{ position: 'relative' }}>
              <span
                onClick={(e) => { e.stopPropagation(); setShowProfileInfo(prev => !prev) }}
                style={{
                  width: '16px', height: '16px', borderRadius: '50%',
                  backgroundColor: showProfileInfo ? '#8b5cf6' : '#e5e7eb',
                  color: showProfileInfo ? '#fff' : '#6b7280',
                  fontSize: '10px', fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s', userSelect: 'none', flexShrink: 0,
                }}
              >
                ?
              </span>

              {showProfileInfo && (
                <div style={{
                  position: 'absolute', top: '22px', right: -120, zIndex: 100,
                  backgroundColor: '#fff', borderRadius: '10px', width: '260px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
                  padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px',
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                    W1 — Before First Query
                  </div>
                  {W1_PROFILES.map((p) => (
                    <div key={p.id} style={{ paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: p.color }} />
                        <span style={{ fontSize: '12px', fontWeight: 700, color: p.color }}>{p.label}</span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.4 }}>{p.description}</div>
                    </div>
                  ))}
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px', marginBottom: '2px' }}>
                    W2 — Between Queries
                  </div>
                  {W2_PROFILES.map((p, i) => (
                    <div key={p.id} style={{ 
                      paddingBottom: i < W2_PROFILES.length - 1 ? '8px' : 0, 
                      borderBottom: i < W2_PROFILES.length - 1 ? '1px solid #f1f5f9' : 'none' 
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: p.color }} />
                        <span style={{ fontSize: '12px', fontWeight: 700, color: p.color }}>{p.label}</span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.4 }}>{p.description}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <span style={{
            fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px',
            backgroundColor: labeledProfileCount === queryWindows.length && queryWindows.length > 0 ? '#dcfce7' : '#f3f4f6',
            color: labeledProfileCount === queryWindows.length && queryWindows.length > 0 ? '#166534' : '#6b7280',
          }}>
            {labeledProfileCount}/{queryWindows.length} labeled
          </span>
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
          Classify the behavioral profile for each pre-query window.
        </div>
      </div>

      {/* Windows list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {queryWindows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: '#9ca3af', border: '2px dashed #e5e7eb', borderRadius: '8px' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.5 }}>📊</div>
            <div style={{ fontSize: '13px' }}>No queries found. Label segments first.</div>
          </div>
        ) : (
          queryWindows.map((window) => {
            const isSelected = selectedProfileQueryId === window.queryId
            const currentProfile = profileLabels[window.queryId]
            const profileInfo = window.profiles.find(p => p.id === currentProfile)
            const isW1 = window.windowType === 'W1'

            return (
              <div
                key={`${window.queryId}-${window.windowType}`}
                onClick={() => handleProfileWindowClick(window)}
                style={{
                  padding: '10px 12px',
                  border: isSelected ? '2px solid #8b5cf6' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: isSelected ? '#f5f3ff' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {/* Window header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{
                      fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px',
                      backgroundColor: isW1 ? '#dbeafe' : '#fef3c7',
                      color: isW1 ? '#1d4ed8' : '#92400e',
                      border: isW1 ? '1px solid #93c5fd' : '1px solid #fcd34d',
                    }}>
                      {isW1 ? 'W1' : 'W2'}
                    </span>
                    <span style={{ fontSize: '11px', color: '#9ca3af', backgroundColor: '#f3f4f6', padding: '1px 6px', borderRadius: '4px' }}>
                      Q{window.queryIndex + 1}
                    </span>
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>
                      {formatTime(window.windowStart)} → {formatTime(window.windowEnd)}
                    </span>
                  </div>
                </div>

                {/* Window context */}
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>
                  {isW1 ? 'Before first query' : `Between query ${window.queryIndex} and ${window.queryIndex + 1}`}
                  {' · '}
                  {formatTime(window.windowDuration)} duration
                </div>

                {/* Behavioral sequence bar */}
                <div style={{ height: '16px', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#e5e7eb', display: 'flex', marginBottom: '6px' }}>
                  {window.segments.length > 0 ? (
                    window.segments.map((seg, idx) => {
                      if (!seg.behavior || window.windowDuration <= 0) {
                        const width = window.windowDuration > 0 
                          ? ((seg.overlapEnd - seg.overlapStart) / window.windowDuration) * 100 
                          : 0
                        return (
                          <div
                            key={idx}
                            title={`Unlabeled: ${formatTime(seg.overlapEnd - seg.overlapStart)}`}
                            style={{ width: `${Math.max(width, 0.5)}%`, height: '100%', backgroundColor: '#d1d5db', minWidth: '2px' }}
                          />
                        )
                      }
                      const width = (( seg.overlapEnd - seg.overlapStart) / window.windowDuration) * 100
                      return (
                        <div
                          key={idx}
                          title={`${seg.behavior.label}: ${formatTime(seg.overlapEnd - seg.overlapStart)}`}
                          style={{ width: `${Math.max(width, 0.5)}%`, height: '100%', backgroundColor: seg.behavior.color, minWidth: '2px' }}
                        />
                      )
                    })
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '9px', color: '#9ca3af' }}>No segments</span>
                    </div>
                  )}
                </div>

                {/* Behavior summary chips */}
                {window.behaviorSummary.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                    {window.behaviorSummary.map((item, idx) => {
                      const pct = window.windowDuration > 0 
                        ? Math.round((item.totalDuration / window.windowDuration) * 100)
                        : 0
                      return (
                        <span
                          key={idx}
                          style={{
                            fontSize: '10px', fontWeight: 500, padding: '1px 6px', borderRadius: '4px',
                            backgroundColor: `${item.behavior.color}15`,
                            color: item.behavior.color,
                            border: `1px solid ${item.behavior.color}40`,
                          }}
                        >
                          {item.behavior.label} {pct}%
                        </span>
                      )
                    })}
                  </div>
                )}

                {/* Profile dropdown */}
                <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                  <div
                    onClick={() => setOpenProfileDropdownId(openProfileDropdownId === window.queryId ? null : window.queryId)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
                      border: `1.5px solid ${currentProfile ? (profileInfo?.color || '#93c5fd') : '#e2e8f0'}`,
                      backgroundColor: '#fff',
                      color: currentProfile ? (profileInfo?.color || '#7c3aed') : '#94a3b8',
                      fontWeight: currentProfile ? 600 : 400,
                      fontSize: '13px',
                      transition: 'all 0.15s',
                      userSelect: 'none',
                    }}
                  >
                    <span>{currentProfile ? profileInfo?.label : (isW1 ? 'Select W1 profile...' : 'Select W2 profile...')}</span>
                    <span style={{ 
                      fontSize: '10px', color: '#94a3b8',
                      transform: openProfileDropdownId === window.queryId ? 'rotate(0deg)' : 'rotate(180deg)',
                      transition: 'transform 0.15s'
                    }}>▲</span>
                  </div>

                  {openProfileDropdownId === window.queryId && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                      backgroundColor: '#fff', borderRadius: '8px', marginTop: '4px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                      border: '1px solid #e2e8f0', overflow: 'hidden',
                    }}>
                      {window.profiles.map((profile, i) => {
                        const isActive = currentProfile === profile.id
                        return (
                          <div
                            key={profile.id}
                            onClick={() => {
                              handleUpdateProfileLabel(window.queryId, profile.id)
                              setOpenProfileDropdownId(null)
                            }}
                            style={{
                              padding: '10px 14px', cursor: 'pointer',
                              borderBottom: i < window.profiles.length - 1 ? '1px solid #f1f5f9' : 'none',
                              backgroundColor: 'transparent',
                              transition: 'background 0.1s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: profile.color, flexShrink: 0 }} />
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: isActive ? 700 : 500, color: isActive ? profile.color : '#374151' }}>
                                  {profile.label}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.3 }}>
                                  {profile.description}
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ display: 'flex', height: '92vh' }}>
        {/* Main Modal */}
        <div style={{
          width: '72vw', backgroundColor: '#1f2937', borderRadius: '12px 0 0 12px',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '16px 24px', borderBottom: '1px solid #374151', flexShrink: 0,
          }}>
            <h2 style={{ margin: 0, color: '#f3f4f6', fontSize: '18px' }}>
              Behavioral Labeling Interface
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button onClick={handleUndo} style={{
                background: 'none', border: '1px solid transparent',
                color: undoStack.length > 0 ? '#9ca3af' : '#4b5563',
                cursor: undoStack.length > 0 ? 'pointer' : 'default',
                padding: '10px', borderRadius: '6px', display: 'flex', alignItems: 'center',
                opacity: undoStack.length > 0 ? 1 : 0.4,
              }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                title="Undo"
              >
                <Undo2 size={26} />
              </button>

              <button onClick={handleRedo} style={{
                background: 'none', border: '1px solid transparent',
                color: redoStack.length > 0 ? '#9ca3af' : '#4b5563',
                cursor: redoStack.length > 0 ? 'pointer' : 'default',
                padding: '10px', borderRadius: '6px', display: 'flex', alignItems: 'center',
                opacity: redoStack.length > 0 ? 1 : 0.4,
              }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                title="Redo"
              >
                <Redo2 size={26} />
              </button>

              <button onClick={onClose} style={{
                background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer',
                padding: '10px', borderRadius: '6px',
              }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <X size={30} />
              </button>
            </div>
          </div>

          {/* Replay Content */}
          <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            <div style={{ width: '133.33%', height: '133.33%', transform: 'scale(0.75)', transformOrigin: 'top left' }}>
              <ReplayWorkspace 
                playback={playback} events={filteredEvents} startTime={startTime}
                task={task} testCases={testCases} starterCode={starterCode} studentName={studentName}
              />
            </div>
          </div>

          {/* Controls */}
          <ReplayControls
            currentTime={playback.currentTime} duration={playback.duration}
            isPlaying={playback.isPlaying} playbackSpeed={playback.playbackSpeed}
            onTogglePlay={playback.togglePlay} onSeek={handleManualSeek}
            onReset={playback.reset} onCycleSpeed={playback.cycleSpeed}
            taggedBlocks={currentBlocks} showLabeling={showLabeling}
            activeRecording={activeRecording} isAutoMode={isAutoMode}
            autoSegments={autoSegments} onAssignBehavior={handleAssignBehavior}
            onAdjustBoundary={handleAdjustBoundary} onSliceSegment={handleSliceSegment}
            formatTime={formatTime} events={filteredEvents} startTime={startTime}
          />
        </div>

        {/* Labeling Sidebar */}
        <div style={{
          width: '300px', backgroundColor: '#ffffff', borderRadius: '0 12px 12px 0',
          overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          display: 'flex', flexDirection: 'column',
        }}>

          {/* Sidebar Tab Toggle */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
            {['labels', 'queries', 'profiles'].map(tab => (
              <button
                key={tab}
                onClick={() => setSidebarTab(tab)}
                style={{
                  flex: 1, padding: '10px', border: 'none', cursor: 'pointer', fontSize: '11px',
                  fontWeight: sidebarTab === tab ? 700 : 500,
                  borderBottom: sidebarTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  color: sidebarTab === tab ? '#3b82f6' : '#6b7280',
                  textTransform: 'capitalize',
                  transition: 'all 0.15s',
                }}
              >
                {tab === 'labels' 
                  ? 'Labels' 
                  : tab === 'queries' 
                    ? `Queries${chatQueries.length > 0 ? ` (${labeledQueryCount}/${chatQueries.length})` : ''}`
                    : `Profiles${queryWindows.length > 0 ? ` (${labeledProfileCount}/${queryWindows.length})` : ''}`
                }
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {sidebarTab === 'labels' ? (
              <StudentLabelingSidebar
                activeRecording={isAutoMode ? null : activeRecording}
                taggedBlocks={currentBlocks}
                handleStartRecording={isAutoMode ? () => {} : handleStartRecording}
                handleStopRecording={isAutoMode ? () => {} : handleStopRecording}
                handleDeleteBlock={handleDeleteBlock}
                handleBlockClick={handleBlockClick}
                handleEditBlock={handleEditBlock}
                formatTime={formatTime}
                onOpenCodebook={() => setCodebookOpen(true)}
                showStartButtons={!isAutoMode}
                showTagsSection={true}
                emptyMessage={isAutoMode 
                  ? "Click segments in the timeline to label them." 
                  : "No tags yet. Start recording to label what you're doing."
                }
                events={filteredEvents}
                startTime={startTime}
                onAssignBehavior={handleAssignBehavior}
                onPause={playback.pause}
              />
            ) : sidebarTab === 'queries' ? (
              renderQueriesTab()
            ) : (
              renderProfilesTab()
            )}
          </div>

          {/* Progress (only show on labels tab) */}
          {sidebarTab === 'labels' && (
            <div style={{ 
              padding: '18px 12px', borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>Progress</span>
                <span style={{ fontSize: '12px', color: labeledCount === currentBlocks.length ? '#10b981' : '#6b7280', fontWeight: 600 }}>
                  {labeledCount}/{currentBlocks.length}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '2px' }}>
                {currentBlocks.map((block) => (
                  <div 
                    key={block.id}
                    onClick={() => playback.seek(block.startTime)}
                    title={`${formatTime(block.startTime)} - ${formatTime(block.endTime)}`}
                    style={{ 
                      flex: 1, height: '8px', borderRadius: '2px', cursor: 'pointer',
                      backgroundColor: block.behavior !== null ? '#3b82f6' : '#e5e7eb',
                      transition: 'transform 0.15s ease',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scaleY(1.3)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scaleY(1)'}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Save Button */}
          <div style={{ padding: '8px 12px 12px 12px', borderTop: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSave}
              disabled={saving || labeledCount === 0 || !labelingSessionId}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                bgcolor: labeledCount === currentBlocks.length ? '#10b981' : '#3b82f6',
                '&:hover': { bgcolor: labeledCount === currentBlocks.length ? '#059669' : '#2563eb' },
                '&:disabled': { bgcolor: '#e5e7eb' },
                textTransform: 'none', fontWeight: 600, py: 1.7, fontSize: '0.9rem',
              }}
            >
              {saving ? 'Saving...' : 'Save Labels'}
            </Button>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <StudentLabelingDialogs
        codebookOpen={codebookOpen} setCodebookOpen={setCodebookOpen}
        stopRecordingOpen={stopRecordingOpen} activeRecording={activeRecording}
        stopRecordingEndTime={stopRecordingEndTime} stopRecordingConfidence={stopRecordingConfidence}
        setStopRecordingConfidence={setStopRecordingConfidence}
        handleStopRecordingConfirm={handleStopRecordingConfirm}
        handleStopRecordingCancel={handleStopRecordingCancel}
        formatTime={formatTime} editDialogOpen={editDialogOpen}
        editingBlock={editingBlock} setEditingBlock={setEditingBlock}
        handleEditConfirm={handleEditConfirm} handleEditCancel={handleEditCancel}
        duration={duration}
      />

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}